// src/hooks/useCanvasHistory.js
import { useState, useRef, useCallback } from "react";
import { Gradient, Rect } from "fabric";

const useCanvasHistory = (fabricCanvasRef, setBackgroundColor) => {
  const historyRef = useRef({ history: [], index: -1 });
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const saveTimeoutRef = useRef(null);
  const isRestoringRef = useRef(false);

  const saveHistory = useCallback((initCanvas) => {
    if (
      !initCanvas ||
      !initCanvas.lowerCanvasEl ||
      !initCanvas.lowerCanvasEl.getContext
    ) {
      console.warn("CanvasHistory: Cannot save history, canvas not ready");
      return;
    }

    if (isRestoringRef.current) {
      console.log("CanvasHistory: Skipping saveHistory during undo/redo");
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      const json = initCanvas.toJSON([
        "id",
        "name",
        "type",
        "fill",
        "stroke",
        "strokeWidth",
        "background",
        "left",
        "top",
        "scaleX",
        "scaleY",
        "radius",
        "width",
        "height",
        "angle",
        "opacity",
        "selectable",
        "evented",
        "originX",
        "originY",
        "flipX",
        "flipY",
        "points",
        "path",
        "shadow",
        "filters",
        "fontFamily",
        "fontSize",
        "fontWeight",
        "fontStyle",
        "textAlign",
        "text",
        "lockMovementX",
        "lockMovementY",
      ]);

      json.backgroundColor =
        typeof initCanvas.backgroundColor === "string" &&
        initCanvas.backgroundColor
          ? initCanvas.backgroundColor
          : initCanvas.backgroundColor &&
            typeof initCanvas.backgroundColor === "object"
          ? {
              type: initCanvas.backgroundColor.type,
              gradientUnits:
                initCanvas.backgroundColor.gradientUnits || "pixels",
              coords: initCanvas.backgroundColor.coords
                ? { ...initCanvas.backgroundColor.coords }
                : {},
              colorStops: initCanvas.backgroundColor.colorStops
                ? [...initCanvas.backgroundColor.colorStops]
                : [],
            }
          : "#ffffff";

      json.viewportTransform = [...initCanvas.viewportTransform];
      json.width = initCanvas.getWidth();
      json.height = initCanvas.getHeight();
      json.canvasWidth = initCanvas.getWidth();
      json.canvasHeight = initCanvas.getHeight();

      const history = historyRef.current.history;
      const currentIndex = historyRef.current.index;
      const newIndex = currentIndex + 1;

      history.splice(newIndex, history.length - newIndex, json);
      historyRef.current.index = newIndex;

      if (history.length > 50) {
        history.shift();
        historyRef.current.index--;
      }

      setCanUndo(newIndex > 0);
      setCanRedo(newIndex < history.length - 1);

      console.log("CanvasHistory: Saving history", {
        historyIndex: newIndex,
        historyLength: history.length,
        backgroundColor: json.backgroundColor,
        canvasWidth: json.canvasWidth,
        canvasHeight: json.canvasHeight,
        viewportTransform: json.viewportTransform,
        zoom: initCanvas.getZoom(),
        objects: json.objects.map((obj) => ({
          type: obj.type,
          id: obj.id,
          name: obj.name,
          left: obj.left,
          top: obj.top,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          radius: obj.radius,
          width: obj.width,
          height: obj.height,
          points: obj.points ? obj.points.length : null,
          path: obj.path ? obj.path.length : null,
        })),
      });
    }, 200);
  }, []);

  const handleUndo = useCallback(() => {
    const initCanvas = fabricCanvasRef.current;
    if (!initCanvas || !initCanvas.lowerCanvasEl?.getContext) {
      console.warn("CanvasHistory: Cannot undo, canvas not ready");
      return;
    }
    const history = historyRef.current.history;
    let historyIndex = historyRef.current.index;
    if (historyIndex <= 0) {
      console.warn("CanvasHistory: No history to undo");
      return;
    }
    historyIndex--;

    isRestoringRef.current = true;

    initCanvas.off("object:added");
    initCanvas.off("object:modified");
    initCanvas.off("object:removed");
    initCanvas.off("selection:created");
    initCanvas.off("selection:updated");
    initCanvas.off("selection:cleared");

    initCanvas.clear();
    const ctx = initCanvas.lowerCanvasEl.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, initCanvas.width, initCanvas.height);
    initCanvas.backgroundColor = "#ffffff";

    const json = history[historyIndex];
    if (!json || !Array.isArray(json.objects)) {
      console.error("CanvasHistory: Invalid JSON state for undo", { json });
      isRestoringRef.current = false;
      return;
    }

    historyRef.current.index = historyIndex;
    setCanUndo(historyIndex > 0);
    setCanRedo(historyIndex < history.length - 1);

    initCanvas.loadFromJSON(
      json,
      () => {
        const backgroundColor = json.backgroundColor || "#ffffff";
        try {
          if (typeof backgroundColor === "object" && backgroundColor.type) {
            initCanvas.backgroundColor = new Gradient(backgroundColor);
          } else {
            initCanvas.backgroundColor = backgroundColor;
          }

          // Manually recreate objects
          const objects = initCanvas.getObjects();
          objects.forEach((obj, i) => {
            if (json.objects[i] && json.objects[i].type === "rect") {
              const rectData = json.objects[i];
              const newRect = new Rect({
                left: rectData.left || 0,
                top: rectData.top || 0,
                width: rectData.width || 100,
                height: rectData.height || 100,
                fill: rectData.fill || "#000000",
                stroke: rectData.stroke,
                strokeWidth: rectData.strokeWidth || 0,
                scaleX: rectData.scaleX || 1,
                scaleY: rectData.scaleY || 1,
                angle: rectData.angle || 0,
                opacity: rectData.opacity || 1,
                selectable: true,
                evented: true,
                type: "rect",
              });
              initCanvas.remove(obj);
              initCanvas.add(newRect);
            } else if (!obj.type && json.objects[i]) {
              console.warn("CanvasHistory: Restored object missing type", {
                obj,
                jsonObj: json.objects[i],
              });
              obj.type = json.objects[i].type || "unknown";
            }
            obj.selectable = true;
            obj.evented = true;
          });

          initCanvas.renderAll();
          initCanvas.renderAll();
          initCanvas.renderAll();
          initCanvas.backgroundColor = backgroundColor;
          setBackgroundColor(backgroundColor);

          // Re-register event listeners
          initCanvas.on("selection:created", (e) => {
            if (initCanvas.__isMounted) {
              initCanvas.__setSelectedObject(e.selected?.[0] || null);
              console.log("CanvasArea: Selection created", {
                target: e.selected?.[0]
                  ? { type: e.selected[0].type, text: e.selected[0].text }
                  : null,
                backgroundColor: initCanvas.backgroundColor,
              });
            }
          });
          initCanvas.on("selection:updated", (e) => {
            if (initCanvas.__isMounted) {
              initCanvas.__setSelectedObject(e.selected?.[0] || null);
              console.log("CanvasArea: Selection updated", {
                target: e.selected?.[0]
                  ? { type: e.selected[0].type, text: e.selected[0].text }
                  : null,
                backgroundColor: initCanvas.backgroundColor,
              });
            }
          });
          initCanvas.on("selection:cleared", () => {
            if (initCanvas.__isMounted) {
              initCanvas.__setSelectedObject(null);
              console.log("CanvasArea: Selection cleared", {
                backgroundColor: initCanvas.backgroundColor,
              });
            }
          });

          console.log("CanvasHistory: Event listeners after undo", {
            selectionCreated:
              initCanvas.__eventListeners["selection:created"]?.length,
            selectionUpdated:
              initCanvas.__eventListeners["selection:updated"]?.length,
            selectionCleared:
              initCanvas.__eventListeners["selection:cleared"]?.length,
          });

          // Manually select first object
          setTimeout(() => {
            const objects = initCanvas.getObjects();
            if (objects.length > 0) {
              initCanvas.setActiveObject(objects[0]);
              initCanvas.renderAll();
              initCanvas.fire("selection:updated", { selected: [objects[0]] });
              console.log("CanvasHistory: Fired selection:updated", {
                selected: {
                  type: objects[0].type,
                  selectable: objects[0].selectable,
                },
              });
            } else {
              initCanvas.fire("selection:cleared");
              console.log("CanvasHistory: Fired selection:cleared");
            }

            console.log("CanvasHistory: Undo completed", {
              historyIndex,
              historyLength: history.length,
              backgroundColor,
              canvasWidth: json.canvasWidth,
              canvasHeight: json.canvasHeight,
              viewportTransform: initCanvas.viewportTransform,
              zoom: initCanvas.getZoom(),
              objects: initCanvas.getObjects().map((obj) => ({
                type: obj.type,
                top: obj.top,
                left: obj.left,
                width: obj.width,
                height: obj.height,
                selectable: obj.selectable,
                evented: obj.evented,
                points: obj.points ? obj.points.length : null,
                path: obj.path ? obj.path.length : null,
              })),
              renderState: initCanvas.lowerCanvasEl.getContext("2d").fillStyle,
            });
          }, 200);
        } catch (error) {
          console.error(
            "CanvasHistory: Failed to set background color on undo",
            {
              error: error.message,
              backgroundColor,
            }
          );
          initCanvas.backgroundColor = "#ffffff";
          initCanvas.renderAll();
        }
      },
      (obj, error) => {
        console.error("CanvasHistory: Error loading object in undo", {
          obj,
          error: error.message,
          jsonObjects: json.objects,
        });
      }
    );

    setTimeout(() => {
      if (initCanvas) {
        initCanvas.on("object:added", () => {
          console.log("CanvasArea: Object added, saving history");
          saveHistory(initCanvas);
        });
        initCanvas.on("object:modified", () => {
          console.log("CanvasArea: Object modified, saving history");
          saveHistory(initCanvas);
        });
        initCanvas.on("object:removed", () => {
          console.log("CanvasArea: Object removed, saving history");
          saveHistory(initCanvas);
        });
        // Listeners already registered above
      }
      isRestoringRef.current = false;
      console.log("CanvasHistory: Events re-enabled after undo");
    }, 1200);
  }, [fabricCanvasRef, setBackgroundColor, saveHistory]);

  const handleRedo = useCallback(() => {
    const initCanvas = fabricCanvasRef.current;
    if (!initCanvas || !initCanvas.lowerCanvasEl?.getContext) {
      console.warn("CanvasHistory: Cannot redo, canvas not ready");
      return;
    }
    const history = historyRef.current.history;
    let historyIndex = historyRef.current.index;
    if (historyIndex >= history.length - 1) {
      console.warn("CanvasHistory: No history to redo");
      return;
    }
    historyIndex++;

    isRestoringRef.current = true;

    initCanvas.off("object:added");
    initCanvas.off("object:modified");
    initCanvas.off("object:removed");
    initCanvas.off("selection:created");
    initCanvas.off("selection:updated");
    initCanvas.off("selection:cleared");

    initCanvas.clear();
    const ctx = initCanvas.lowerCanvasEl.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, initCanvas.width, initCanvas.height);
    initCanvas.backgroundColor = "#ffffff";

    const json = history[historyIndex];
    if (!json || !Array.isArray(json.objects)) {
      console.error("CanvasHistory: Invalid JSON state for redo", { json });
      isRestoringRef.current = false;
      return;
    }

    historyRef.current.index = historyIndex;
    setCanUndo(historyIndex > 0);
    setCanRedo(historyIndex < history.length - 1);

    initCanvas.loadFromJSON(
      json,
      () => {
        const backgroundColor = json.backgroundColor || "#ffffff";
        try {
          if (typeof backgroundColor === "object" && backgroundColor.type) {
            initCanvas.backgroundColor = new Gradient(backgroundColor);
          } else {
            initCanvas.backgroundColor = backgroundColor;
          }

          // Manually recreate objects
          const objects = initCanvas.getObjects();
          objects.forEach((obj, i) => {
            if (json.objects[i] && json.objects[i].type === "rect") {
              const rectData = json.objects[i];
              const newRect = new Rect({
                left: rectData.left || 0,
                top: rectData.top || 0,
                width: rectData.width || 100,
                height: rectData.height || 100,
                fill: rectData.fill || "#000000",
                stroke: rectData.stroke,
                strokeWidth: rectData.strokeWidth || 0,
                scaleX: rectData.scaleX || 1,
                scaleY: rectData.scaleY || 1,
                angle: rectData.angle || 0,
                opacity: rectData.opacity || 1,
                selectable: true,
                evented: true,
                type: "rect",
              });
              initCanvas.remove(obj);
              initCanvas.add(newRect);
            } else if (!obj.type && json.objects[i]) {
              console.warn("CanvasHistory: Restored object missing type", {
                obj,
                jsonObj: json.objects[i],
              });
              obj.type = json.objects[i].type || "unknown";
            }
            obj.selectable = true;
            obj.evented = true;
          });

          initCanvas.renderAll();
          initCanvas.renderAll();
          initCanvas.renderAll();
          initCanvas.backgroundColor = backgroundColor;
          setBackgroundColor(backgroundColor);

          // Re-register event listeners
          initCanvas.on("selection:created", (e) => {
            if (initCanvas.__isMounted) {
              initCanvas.__setSelectedObject(e.selected?.[0] || null);
              console.log("CanvasArea: Selection created", {
                target: e.selected?.[0]
                  ? { type: e.selected[0].type, text: e.selected[0].text }
                  : null,
                backgroundColor: initCanvas.backgroundColor,
              });
            }
          });
          initCanvas.on("selection:updated", (e) => {
            if (initCanvas.__isMounted) {
              initCanvas.__setSelectedObject(e.selected?.[0] || null);
              console.log("CanvasArea: Selection updated", {
                target: e.selected?.[0]
                  ? { type: e.selected[0].type, text: e.selected[0].text }
                  : null,
                backgroundColor: initCanvas.backgroundColor,
              });
            }
          });
          initCanvas.on("selection:cleared", () => {
            if (initCanvas.__isMounted) {
              initCanvas.__setSelectedObject(null);
              console.log("CanvasArea: Selection cleared", {
                backgroundColor: initCanvas.backgroundColor,
              });
            }
          });

          console.log("CanvasHistory: Event listeners after redo", {
            selectionCreated:
              initCanvas.__eventListeners["selection:created"]?.length,
            selectionUpdated:
              initCanvas.__eventListeners["selection:updated"]?.length,
            selectionCleared:
              initCanvas.__eventListeners["selection:cleared"]?.length,
          });

          // Manually select first object
          setTimeout(() => {
            const objects = initCanvas.getObjects();
            if (objects.length > 0) {
              initCanvas.setActiveObject(objects[0]);
              initCanvas.renderAll();
              initCanvas.fire("selection:updated", { selected: [objects[0]] });
              console.log("CanvasHistory: Fired selection:updated", {
                selected: {
                  type: objects[0].type,
                  selectable: objects[0].selectable,
                },
              });
            } else {
              initCanvas.fire("selection:cleared");
              console.log("CanvasHistory: Fired selection:cleared");
            }

            console.log("CanvasHistory: Redo completed", {
              historyIndex,
              historyLength: history.length,
              backgroundColor,
              canvasWidth: json.canvasWidth,
              canvasHeight: json.canvasHeight,
              viewportTransform: initCanvas.viewportTransform,
              zoom: initCanvas.getZoom(),
              objects: initCanvas.getObjects().map((obj) => ({
                type: obj.type,
                top: obj.top,
                left: obj.left,
                width: obj.width,
                height: obj.height,
                selectable: obj.selectable,
                evented: obj.evented,
                points: obj.points ? obj.points.length : null,
                path: obj.path ? obj.path.length : null,
              })),
              renderState: initCanvas.lowerCanvasEl.getContext("2d").fillStyle,
            });
          }, 200);
        } catch (error) {
          console.error(
            "CanvasHistory: Failed to set background color on redo",
            {
              error: error.message,
              backgroundColor,
            }
          );
          initCanvas.backgroundColor = "#ffffff";
          initCanvas.renderAll();
        }
      },
      (obj, error) => {
        console.error("CanvasHistory: Error loading object in redo", {
          obj,
          error: error.message,
          jsonObjects: json.objects,
        });
      }
    );

    setTimeout(() => {
      if (initCanvas) {
        initCanvas.on("object:added", () => {
          console.log("CanvasArea: Object added, saving history");
          saveHistory(initCanvas);
        });
        initCanvas.on("object:modified", () => {
          console.log("CanvasArea: Object modified, saving history");
          saveHistory(initCanvas);
        });
        initCanvas.on("object:removed", () => {
          console.log("CanvasArea: Object removed, saving history");
          saveHistory(initCanvas);
        });
        // Listeners already registered above
      }
      isRestoringRef.current = false;
      console.log("CanvasHistory: Events re-enabled after redo");
    }, 1200);
  }, [fabricCanvasRef, setBackgroundColor, saveHistory]);

  const clearHistory = useCallback(() => {
    const initCanvas = fabricCanvasRef.current;
    if (initCanvas) {
      const json = {
        version: "6.4.2",
        objects: [],
        backgroundColor: "#ffffff",
        width: initCanvas.getWidth(),
        height: initCanvas.getHeight(),
        canvasWidth: initCanvas.getWidth(),
        canvasHeight: initCanvas.getHeight(),
        viewportTransform: [1, 0, 0, 1, 0, 0],
      };
      historyRef.current.history = [json];
      historyRef.current.index = 0;
      setCanUndo(false);
      setCanRedo(false);
      console.log("CanvasHistory: History cleared with initial state", {
        historyIndex: 0,
        historyLength: 1,
        backgroundColor: json.backgroundColor,
        canvasWidth: json.canvasWidth,
        canvasHeight: json.canvasHeight,
        viewportTransform: json.viewportTransform,
      });
    } else {
      historyRef.current.history = [];
      historyRef.current.index = -1;
      setCanUndo(false);
      setCanRedo(false);
      console.log("CanvasHistory: History cleared (no canvas)");
    }
  }, [fabricCanvasRef]);

  const getCurrentHistoryState = useCallback(() => {
    const { history, index } = historyRef.current;
    return history[index] || null;
  }, []);

  const debugHistory = useCallback(() => {
    console.log("CanvasHistory: Current history state", {
      history: historyRef.current.history.map((state, i) => ({
        index: i,
        objects: state.objects.map((obj) => ({
          type: obj.type,
          width: obj.width,
          height: obj.height,
        })),
      })),
      currentIndex: historyRef.current.index,
      canUndo,
      canRedo,
    });
  }, [canUndo, canRedo]);

  return {
    saveHistory,
    handleUndo,
    handleRedo,
    clearHistory,
    getCurrentHistoryState,
    debugHistory,
    canUndo,
    canRedo,
  };
};

export default useCanvasHistory;
